import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { MdCheckCircle, MdOutlineCancelScheduleSend } from "react-icons/md";
import { Link } from "react-router-dom";
import { Badge, Card, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import { getDnhOrderStatus } from "../../api/edi.api";
import {
  getShippingIdentifierShort,
  isComingToTechMicro,
} from "../../utils/shippingIdentifier";

const PoDetails = ({
  data,
  lineIndex,
  line_items,
  setLineItems,
  orderData,
  product,
  length,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dnhStatuses, setDnhStatuses] = useState({});
  const [poReceivedMap, setPoReceivedMap] = useState({});

  useEffect(() => {
    if (product?.po_received) {
      setPoReceivedMap(product.po_received);
    } else if (
      product?.quantity_received !== undefined ||
      product?.quantityReceived !== undefined
    ) {
      const defaultQty =
        product?.quantity_received ?? product?.quantityReceived ?? 0;
      if (data?.length === 1 && data[0]?._id) {
        setPoReceivedMap({ [data[0]._id]: defaultQty });
      }
    }
  }, [product, data]);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const fetchDnhStatus = async (po) => {
    if (!po?.dnhOrderId || dnhStatuses[po._id]?.status) return;
    setDnhStatuses((prev) => ({
      ...prev,
      [po._id]: { loading: true, status: null },
    }));
    getDnhOrderStatus(po.dnhOrderId, (res) => {
      if (res?.status === 200) {
        setDnhStatuses((prev) => ({
          ...prev,
          [po._id]: { loading: false, status: res.data?.data?.orderStatus },
        }));
      } else {
        setDnhStatuses((prev) => ({
          ...prev,
          [po._id]: { loading: false, status: "Error" },
        }));
      }
    });
  };

  const findQuantitiesAndPriceOfPO = (id) => {
    if (data?.length > 0) {
      const po = data.find((i) => i?._id === id);
      if (po) {
        const poLineItems = po?.line_items?.filter(
          (kk) =>
            kk?.sku?.trim()?.toUpperCase() ===
            product?.sku?.trim()?.toUpperCase()
        );
        return poLineItems
          .map((poItem) => {
            const matchedItem = line_items?.find(
              (item) =>
                item?.sku?.trim()?.toUpperCase() ===
                poItem?.sku?.trim()?.toUpperCase()
            );
            if (matchedItem) {
              const qty = parseInt(poItem?.quantity) || 0;
              const price = parseFloat(poItem?.price) || 0;
              const itemTotal =
                poItem?.total !== undefined &&
                poItem?.total !== null &&
                !isNaN(parseFloat(poItem?.total))
                  ? parseFloat(poItem?.total)
                  : qty * price;
              return {
                sku: poItem.sku,
                quantity: qty,
                price: price,
                total: itemTotal,
              };
            }
            return null;
          })
          .filter((item) => item !== null);
      }
    }
    return [];
  };

  const handleReceivedChange = (poId, val) => {
    setPoReceivedMap((prev) => ({
      ...prev,
      [poId]: val,
    }));
  };

  const handleReceivedSave = async (poId, rawVal, maxQty) => {
    let parsedVal = isNaN(parseInt(rawVal, 10)) ? 0 : Math.max(0, parseInt(rawVal, 10));
    const max = parseInt(maxQty ?? 0, 10);
    if (parsedVal > max) {
      toast.error(`Quantity received (${parsedVal}) cannot exceed PO quantity (${max})`);
      parsedVal = max;
    }

    setPoReceivedMap((prev) => ({
      ...prev,
      [poId]: parsedVal,
    }));

    if (!line_items || lineIndex === undefined || lineIndex < 0) return;

    const currentItem = line_items[lineIndex] || product || {};
    const updatedPoReceived = {
      ...(currentItem?.po_received || {}),
      [poId]: parsedVal,
    };
    const totalRec = Object.values(updatedPoReceived).reduce(
      (sum, v) => sum + (parseInt(v, 10) || 0),
      0
    );

    const updatedLineItem = {
      ...currentItem,
      po_received: updatedPoReceived,
      quantity_received: totalRec,
      quantityReceived: totalRec,
    };

    const updatedLineItems = line_items.map((item, idx) =>
      idx === lineIndex ? updatedLineItem : item
    );

    if (setLineItems) {
      setLineItems(updatedLineItems);
    }

    if (orderData?._id) {
      try {
        const collection = orderData?.return_status ? "ReturnOrders" : "Orders";
        await firebase
          .firestore()
          .collection(collection)
          .doc(orderData._id)
          .update({ line_items: updatedLineItems });
        toast.success("Quantity received updated successfully");
      } catch (err) {
        console.error("Failed to save quantity received:", err);
        toast.error("Failed to save quantity received");
      }
    }
  };

  useEffect(() => {
    if (data?.length > 0) {
      data.forEach((po) => {
        if (po?.dnhOrderStatus === "submitted") {
          fetchDnhStatus(po);
        }
      });
    }
  }, [data]);

  return (
    <>
      {data?.length > 0 ? (
        <Row className="align-items-center ">
          <Col md={1}>
            <div className="d-flex align-items-center gap-2">
              <Label className="mt-2 mb-2 fw-bold">PO's </Label>
              <div
                className="mt-2 mb-2 fw-bold fs-18 text-success bg-success rounded-pill text-white d-flex justify-content-center align-items-center"
                style={{ height: "40px", width: "40px" }}
              >
                {length ?? 0}
              </div>
            </div>
          </Col>
          <Col md={11}>
            <Card className="p-0 m-0 bg-secondary-color ">
              <Table
                responsive
                hover
                style={{
                  fontSize: "13px",
                  overflow: "hidden",
                }}
                className="mb-0"
              >
                <thead style={{ backgroundColor: "#00509d2E" }}>
                  <tr>
                    <th>
                      {isExpanded ? (
                        <div className="d-flex align-items-center gap-3">
                          <FaChevronUp
                            onClick={toggleExpand}
                            className="text-primary pointer"
                          />
                          <Label className="mb-0 fw-bold">Show Less</Label>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-3">
                          <FaChevronDown
                            onClick={toggleExpand}
                            className="text-primary pointer"
                          />
                          <Label className=" fw-bold mb-0">Show More</Label>
                        </div>
                      )}
                    </th>
                    <th>Pdf Status</th>
                    <th>Action</th>
                    <th>PO</th>
                    <th>Vendor</th>
                    <th>Vendor PO Number</th>
                    <th>Quantity</th>
                    <th>Qty Rec.</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data
                    .sort((a, b) => {
                      if (
                        a.purchaseOrderStatus === "active" &&
                        b.purchaseOrderStatus !== "active"
                      )
                        return -1;
                      if (
                        b.purchaseOrderStatus === "active" &&
                        a.purchaseOrderStatus !== "active"
                      )
                        return 1;
                      return new Date(b.created_at) - new Date(a.created_at);
                    })
                    .slice(0, isExpanded ? data.length : 1)
                    .map((row, index) => {
                      const matchedItems = findQuantitiesAndPriceOfPO(row?._id);
                      const isComingToTM = isComingToTechMicro(row);
                      const shippingLabel = getShippingIdentifierShort(row);
                      return (
                        <React.Fragment key={index}>
                          {matchedItems.length > 0 ? (
                            matchedItems.map((matchedItem, itemIndex) => (
                              <tr key={itemIndex}>
                                <td style={{ verticalAlign: "middle" }}>
                                  <div className="d-flex flex-column align-items-start gap-1">
                                    <span style={{ fontWeight: 500 }}>
                                      {moment(row?.created_at).format("L")}
                                    </span>
                                    <span
                                      style={{
                                        display: "inline-block",
                                        fontSize: "10.5px",
                                        fontWeight: 600,
                                        padding: "2px 7px",
                                        borderRadius: "4px",
                                        whiteSpace: "nowrap",
                                        lineHeight: "1.25",
                                        backgroundColor: isComingToTM
                                          ? "#dbeafe"
                                          : "#dcfce7",
                                        color: isComingToTM
                                          ? "#1d4ed8"
                                          : "#166534",
                                        border: `1px solid ${
                                          isComingToTM ? "#bfdbfe" : "#86efac"
                                        }`,
                                      }}
                                    >
                                      {shippingLabel}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  {row?.sendPdf ? (
                                    <MdCheckCircle
                                      size={25}
                                      className=" text-success "
                                    />
                                  ) : (
                                    <MdOutlineCancelScheduleSend
                                      size={20}
                                      className="text-danger"
                                    />
                                  )}
                                </td>
                                <td>
                                  <Link
                                    to={{
                                      pathname: `/admin/update-purchase-orders-details/${row?._id}`,
                                      state: {
                                        obj: row,
                                        vendor: {
                                          billing: [row?.vendor],
                                          id: row?.vendorOrderId,
                                          _id: row?.vendorId,
                                        },
                                      },
                                    }}
                                    className="text-decoration-none primary-color"
                                  >
                                    View
                                  </Link>
                                  <Link
                                    to={{
                                      pathname: `/admin/generate-purchase-orders-details/${row?._id}`,
                                      state: {
                                        obj: row,
                                        id: row?.id,
                                        vendor: {
                                          billing: [row?.vendor],
                                          id: row?.vendorOrderId,
                                          _id: row?.vendorId,
                                        },
                                      },
                                    }}
                                    className="text-decoration-none primary-color mx-3"
                                  >
                                    PDF
                                  </Link>
                                </td>
                                <td>{row?.po_no}</td>
                                <td>{row?.vendor?.company}</td>
                                <td>{row?.vendorPoNumber}</td>
                                <td>{matchedItem.quantity}</td>
                                <td style={{ minWidth: "85px" }}>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={matchedItem.quantity}
                                    className="rounded-0 text-center py-1 px-2"
                                    style={{
                                      width: "70px",
                                      height: "28px",
                                      fontSize: "13px",
                                    }}
                                    value={
                                      poReceivedMap[row?._id] !== undefined
                                        ? poReceivedMap[row?._id]
                                        : (product?.po_received?.[row?._id] ??
                                          product?.quantity_received ??
                                          product?.quantityReceived ??
                                          0)
                                    }
                                    onChange={(e) =>
                                      handleReceivedChange(row?._id, e.target.value)
                                    }
                                    onBlur={(e) =>
                                      handleReceivedSave(
                                        row?._id,
                                        e.target.value,
                                        matchedItem.quantity
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.target.blur();
                                      }
                                    }}
                                  />
                                </td>
                                <td>${matchedItem.price?.toFixed(2)}</td>
                                <td>
                                  $
                                  {(
                                    matchedItem.total ??
                                    matchedItem.quantity * matchedItem.price ??
                                    0
                                  ).toFixed(2)}
                                </td>
                                <td>
                                  {row?.dnhOrderStatus === "submitted" ? (
                                    dnhStatuses[row._id]?.loading ? (
                                      <Spinner size="sm" color="primary" />
                                    ) : (
                                      <Badge color="info">
                                        {dnhStatuses[row._id]?.status ??
                                          "Fetching..."}
                                      </Badge>
                                    )
                                  ) : row?.purchaseOrderStatus === "cancel" ? (
                                    <Badge color="danger">Cancelled</Badge>
                                  ) : row?.purchaseOrderStatus === "active" ? (
                                    "Active"
                                  ) : (
                                    "Inactive"
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="11" className="text-center">
                                No matched items
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      ) : null}
    </>
  );
};
export default PoDetails;
